import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { type FailureActionData, apiAction } from "@client/lib/actions";
import { type BaseAppState, type EntityRef, Status } from "@client/types";
import { isStaffOrSuperUser } from "@common/appInfo";
import toasts from "@common/lib/toast";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import { DUMMY_ORDER, isDummyOrder } from "../OrderConfirmation/lib";
import api from "./api";
import { previewState } from "./reducer";
import { localStateSelector } from "./selectors";
import { type MemberRecurringOrders } from "./types";

export enum Actions {
  InitBegin = "MEMBER_RECURRING_ORDERS_INIT_BEGIN",
  InitFailure = "MEMBER_RECURRING_ORDERS_INIT_FAILURE",
  InitSuccess = "MEMBER_RECURRING_ORDERS_INIT_SUCCESS",

  GetDetailBegin = "MEMBER_RECURRING_ORDERS_GET_DETAIL_BEGIN",
  GetDetailFailure = "MEMBER_RECURRING_ORDERS_GET_DETAIL_FAILURE",
  GetDetailSuccess = "MEMBER_RECURRING_ORDERS_GET_DETAIL_SUCCESS",

  SetShowingDetailId = "MEMBER_RECURRING_ORDERS_SET_SHOWING_DETAIL_ID_BEGIN",

  UpdateDetailBegin = "UpdateMEMBER_RECURRING_ORDERS_UPDATE_DETAIL_BEGIN",
  UpdateDetailFailure = "UpdateMEMBER_RECURRING_ORDERS_UPDATE_DETAIL_FAILURE",
  UpdateDetailSuccess = "UpdateMEMBER_RECURRING_ORDERS_UPDATE_DETAIL_SUCCESS"
}

export type InitBeginActionData = EntityRef;
export interface InitSuccessActionData {
  items: MemberRecurringOrders.Item[];
}
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  MemberRecurringOrders.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: BaseAppState, dispatch: any, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    return api.init(apiInfo, {});
  },
  mapResponse: (state: BaseAppState, resp: MemberRecurringOrders.Api.InitResponse) => {
    const res = resp;

    if (!res.items.length && isStaffOrSuperUser()) {
      res.items = previewState.items;
    }

    return res;
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type GetDetailBeginActionData = EntityRef & {
  itemId: string;
};
export type GetDetailSuccessActionData = MemberRecurringOrders.Api.GetDetailResponse & {};
export const _getDetail = apiAction<
  BaseAppState,
  GetDetailBeginActionData,
  MemberRecurringOrders.Api.GetDetailResponse,
  GetDetailSuccessActionData
>({
  types: {
    begin: Actions.GetDetailBegin,
    failure: Actions.GetDetailFailure,
    success: Actions.GetDetailSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: GetDetailBeginActionData) => {
    if (isDummyOrder(params.itemId)) {
      const localState = localStateSelector(state, params);
      const item = localState.items.find((x) => x.id === params.itemId)!;
      return Promise.resolve({
        status: 200,
        response: {
          data: {
            ...DUMMY_ORDER,
            subscription: {
              ...item,
              productVariantId: "",
              quantity: 1,
              repeatInterval: 1,
              repeatIntervalTimeUnitKey: "Month",
              repeatLimit: null,
              subscriptionId: "",
              subscriptionStatusKey: "Active"
            }
          },
          status: "success"
        }
      });
    }

    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.getDetail(apiInfo, { id: params.itemId });
  }
});

export type GetDetailArgs = EntityRef & {
  itemId: string;
};
export const getDetail = (args: GetDetailArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (!(args.itemId in localState.detail)) {
    dispatch(_getDetail(args));
  }
};

export type SetShowingDetailIdActionData = EntityRef & {
  itemId: string | null;
};
export const setShowingDetailId = (args: SetShowingDetailIdActionData) => (dispatch: Dispatch<any>) => {
  if (args.itemId) dispatch(getDetail({ id: args.id, source: args.source, itemId: args.itemId }));

  dispatch({
    type: Actions.SetShowingDetailId,
    data: <SetShowingDetailIdActionData>args
  });
};

export type UpdateDetailBeginActionData = EntityRef & {
  itemId: string;
  data: MemberRecurringOrders.View.UpdateDetail;
};
export interface UpdateDetailSuccessActionData {}
export type UpdateDetailFailureActionData = FailureActionData;
const _updateDetail = apiAction<BaseAppState, UpdateDetailBeginActionData, UpdateDetailSuccessActionData>({
  types: {
    begin: Actions.UpdateDetailBegin,
    failure: Actions.UpdateDetailFailure,
    success: [
      Actions.UpdateDetailSuccess,
      () => {
        toasts.saved();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, args: UpdateDetailBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    return api.updateDetail(apiInfo, {
      id: args.itemId,
      ...{
        maxRepeat: args.data.maxRepeat,
        nextRecurringDate: args.data.nextRecurringDate,
        productVariantId: args.data.productVariantId,
        quantity: args.data.quantity,
        status: args.data.status
      }
    });
  }
});

export const updateDetail =
  (ref: EntityRef, data: MemberRecurringOrders.View.UpdateDetail) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== Status.Saving) {
      dispatch(_updateDetail({ ...ref, itemId: localState.showingDetailId!, data }));
    }
  };

export type DownloadInvoiceActionData = EntityRef & {};
export const downloadInvoice = (args: EntityRef) => async (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.showingDetailId) {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    await api.downloadInvoice(apiInfo, localState.showingDetailId);
  }
};
